import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { formatSecs } from '../../lib/utils';
import classNames from 'classnames';
import Icon from '../ds/Icon';

const SORT_LABELS = {
    Attention: 'Needs attention',
    Name: 'Name (A–Z)',
    Logged: 'Time logged',
    Done: 'Completion',
};

const Bullet = ({ pct, elapsed, tone }) => (
    <div className="bullet">
        <div className={classNames('bullet__fill', tone && `is-${tone}`)} style={{ width: `${Math.min(100, pct)}%` }} />
        <div className="bullet__marker" style={{ left: `${Math.min(100, elapsed)}%` }} />
    </div>
);

const RANK = { bad: 0, warn: 1, good: 2, mute: 3 };

const MemberStats = ({ progVal, tmTotal, prog, statsList, milestone }) => {
    const [sortMode, setSortMode] = useState('Attention');
    const [sp] = useSearchParams();
    const elapsed = Math.min(100, Number(prog) || 0);

    const rows = statsList.map(item => {
        const s = item.getStats(milestone);
        const capacity = s.tmTotal || 0;
        const est = s.vals.estimate || 0;
        const spent = s.vals.spent || 0;
        const total = s.count || 0;
        const done = s.vals.completed || 0;
        const donePct = Number(s.perc.completed) || 0;
        const loggedPct = capacity ? (spent * 100 / capacity) : 0;
        const loadPct = capacity ? (est * 100 / capacity) : 0;

        let status = 'On track', tone = 'good';
        if (total === 0) { status = 'No tasks'; tone = 'mute'; }
        else if (done === total) { status = 'Done'; tone = 'good'; }
        else if (loadPct > 105) { status = 'Over-committed'; tone = 'warn'; }
        else if (loggedPct < elapsed - 20 || donePct < elapsed - 30) { status = 'Behind'; tone = 'bad'; }
        else if (loggedPct < elapsed - 5) { status = 'Watch'; tone = 'warn'; }

        const barTone = loggedPct >= elapsed - 5 ? 'good' : loggedPct >= elapsed - 20 ? 'warn' : 'bad';
        return { s, capacity, est, spent, total, done, donePct, loggedPct, loadPct, status, tone, barTone };
    });

    rows.sort((a, b) => {
        if (sortMode === 'Name') return a.s.name.localeCompare(b.s.name);
        if (sortMode === 'Logged') return b.spent - a.spent;
        if (sortMode === 'Done') return b.donePct - a.donePct;
        // Attention: needs-attention first, then least logged
        return (RANK[a.tone] - RANK[b.tone]) || (a.loggedPct - b.loggedPct);
    });

    // Sprint-level rollups for the KPI row.
    const agg = rows.reduce((a, r) => {
        a.tasks += r.total;
        a.done += r.done;
        a.spent += r.spent;
        a.capacity += r.capacity;
        a.toPlan += (r.s.vals.toPlan || 0);
        if (r.spent === 0 && r.total > 0) a.idle += 1;       // members yet to log any time
        return a;
    }, { tasks: 0, done: 0, spent: 0, capacity: 0, toPlan: 0, idle: 0 });
    const completionPct = agg.tasks ? Math.round((agg.done * 100) / agg.tasks) : 0;
    const utilPct = agg.capacity ? Math.round((agg.spent * 100) / agg.capacity) : 0;

    return (
        <div className="ds-panel">
            <div className="ds-panel__head">
                <h3 className="ds-panel__title">Team Progress</h3>
                <label className="ds-sort">
                    <Icon name="sort" className="ds-sort__icon" title="Sort team" />
                    <select className="ds-select ds-sort__select" value={sortMode} onChange={e => setSortMode(e.target.value)} aria-label="Sort team by">
                        {['Attention', 'Name', 'Logged', 'Done'].map(o => (
                            <option key={o} value={o}>{SORT_LABELS[o]}</option>
                        ))}
                    </select>
                </label>
            </div>
            <div className="tp-stats">
                <div className="tp-stats__cap">{elapsed}% of sprint elapsed</div>
                <div className="tp-stats__grid">
                <div className="tp-stat">
                    <span className="tp-stat__n">{completionPct}<small>%</small></span>
                    <span className="tp-stat__l">Tasks done</span>
                    <span className="tp-stat__s">{agg.done} of {agg.tasks}</span>
                </div>
                <div className="tp-stat" title="Time logged against team capacity">
                    <span className={classNames('tp-stat__n', { 'is-warn': utilPct > 100 })}>{utilPct}<small>%</small></span>
                    <span className="tp-stat__l">Utilisation</span>
                    <span className="tp-stat__s">{formatSecs(agg.spent)} / {formatSecs(agg.capacity)}</span>
                </div>
                <div className="tp-stat" title="Tasks without a time estimate">
                    <span className={classNames('tp-stat__n', { 'is-warn': agg.toPlan > 0 })}>{agg.toPlan}</span>
                    <span className="tp-stat__l">To plan</span>
                    <span className="tp-stat__s">unestimated</span>
                </div>
                <div className="tp-stat" title="Members with tasks but no time logged yet">
                    <span className={classNames('tp-stat__n', { 'is-bad': agg.idle > 0 })}>{agg.idle}</span>
                    <span className="tp-stat__l">Not logged</span>
                    <span className="tp-stat__s">no time yet</span>
                </div>
                </div>
            </div>
            <div className="ds-panel__body">
                {rows.length === 0 && <div className="tp-empty">Add tasks and update completion status to show progress here</div>}
                {rows.map((r, idx) => (
                    <div className="tp-row" key={`${r.s.uname}-${idx}`}>
                        <div className="tp-top">
                            <span className="tp-name">
                                <span className={`tp-dot is-${r.tone}`} />
                                <Link to={`/member/${r.s.uname}/?${sp.toString()}`}>{r.s.name}</Link>
                                {r.s.vals.toPlan > 0 && <span className="ds-badge tp-plan" title="tasks without estimate">⚑ {r.s.vals.toPlan} to plan</span>}
                            </span>
                            <span className="tp-load">
                                <span className="tp-load__item"><span className="tp-load__k">Planned</span> <b>{formatSecs(r.est)}</b></span>
                                <span className="tp-load__item"><span className="tp-load__k">Logged</span> <b>{formatSecs(r.spent)}</b></span>
                                {r.loadPct > 105 && <span className="tp-over">over-committed</span>}
                            </span>
                        </div>
                        <div className="tp-bottom">
                            <div className="tp-bar" title={`Logged ${formatSecs(r.spent)} of ${formatSecs(r.capacity)} capacity (${Math.round(r.loggedPct)}%) · Planned ${formatSecs(r.est)} · ${elapsed}% of sprint elapsed`}>
                                <Bullet pct={r.loggedPct} elapsed={elapsed} tone={r.barTone} />
                            </div>
                            <span className="tp-done" title="tasks completed">
                                <span className="tp-ring" style={{ '--p': r.donePct }} />
                                {r.done}/{r.total}
                            </span>
                            <span className={`tp-status is-${r.tone}`}>{r.status}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default MemberStats;
